import * as React from 'react'
import {
    ArrowRight,
    Check,
    ChevronUp,
    Copy as CopyIcon,
    Info,
    Loader2,
    Plug,
} from 'lucide-react'
import { Select, Tooltip } from '@mui/material'

import { UserContext } from '../../UserContext'
import { useSnackbar } from '../Snackbar'
import { sendToAnalytics } from '../../Analytics'
import { useOnboardingStore } from '../../stores/useOnboardingStore'
import { userLanguageMenuItems } from '../../home/SettingsTemplates'
import { postUserLanguage } from './postUserLanguage'
import {
    LANGUAGE_HINT,
    MCP_CLIENTS,
    MCP_SETUP_INTRO,
    getStepNames,
} from './copy'
import ToastCard, { ToastAnchor } from './ToastCard'
import {
    ACCENT_DOT,
    ACCENT_TEXT,
    ACCENT_TINT,
    ACTION_BUTTON,
    AI_ACCENT_TEXT,
    AI_ACCENT_TINT,
    AI_ACTION_BUTTON,
    BODY_TEXT,
    DONE_TEXT,
    GHOST_BUTTON,
    META_TEXT,
    SECONDARY_TEXT,
    TITLE_TEXT,
    TOAST_FOOTER,
    TOAST_MENU_Z_INDEX,
    choiceTabClass,
} from './toastStyles'

const selectorShowSnackbar = state => state.showSnackbar

function LanguagePicker() {
    const { inlanguage } = React.useContext(UserContext)
    const showSnackbar = useSnackbar(selectorShowSnackbar)
    const { username } = React.useContext(UserContext)
    const [language, setLanguage] = React.useState(inlanguage || 'auto')

    React.useEffect(() => {
        if (inlanguage) setLanguage(inlanguage)
    }, [inlanguage])

    const handleChange = event => {
        const nextLanguage = event.target.value
        setLanguage(nextLanguage)
        postUserLanguage(nextLanguage)
            .then(() => {
                showSnackbar('success', `Language changed to ${nextLanguage}.`)
                sendToAnalytics({
                    userId: username,
                    event: 'change_user_settings',
                    data: { type: 'language_settings' },
                })
            })
            .catch(() => {
                showSnackbar('error', 'Failed to save the language setting.')
            })
    }

    return (
        <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2.5 tw-gap-y-1.5">
            <label
                htmlFor="onboarding-language"
                className={`${META_TEXT} tw-text-muted-foreground`}
            >
                Set your input language
            </label>
            <Select
                id="onboarding-language"
                size="small"
                value={language}
                onChange={handleChange}
                // Portalled onto MUI's modal layer; must be lifted above the toast.
                MenuProps={{ sx: { zIndex: TOAST_MENU_Z_INDEX } }}
                sx={{ fontSize: 13, height: 30, borderRadius: 99 }}
            >
                {userLanguageMenuItems()}
            </Select>
            <Tooltip
                title={LANGUAGE_HINT}
                // Touch devices never hover.
                enterTouchDelay={0}
                leaveTouchDelay={6000}
            >
                <button
                    type="button"
                    aria-label={LANGUAGE_HINT}
                    className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-text-muted-foreground tw-transition-colors hover:tw-bg-muted hover:tw-text-foreground"
                >
                    <Info className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                </button>
            </Tooltip>
        </div>
    )
}

function McpSetupCard({ initialClientId }) {
    const [activeId, setActiveId] = React.useState(
        initialClientId || MCP_CLIENTS[0].id
    )
    const [copied, setCopied] = React.useState(false)
    const active =
        MCP_CLIENTS.find(client => client.id === activeId) || MCP_CLIENTS[0]

    const copySnippet = () => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1800)
        if (navigator.clipboard) {
            navigator.clipboard.writeText(active.snippet).catch(() => undefined)
        }
    }

    return (
        <div className="tw-mt-4 tw-space-y-3 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-muted tw-p-3">
            {MCP_SETUP_INTRO.map(paragraph => (
                <p
                    key={paragraph}
                    className={`tw-m-0 ${BODY_TEXT} ${SECONDARY_TEXT}`}
                >
                    {paragraph}
                </p>
            ))}
            <p
                className={`tw-m-0 ${META_TEXT} tw-leading-relaxed tw-text-muted-foreground`}
            >
                You’ll need an API key. Create one on your{' '}
                {/* New tab on purpose: navigating away from the graph unmounts
                    the guide, and it comes back with this setup card closed. */}
                <a href="/api-access" target="_blank" className={ACCENT_TEXT}>
                    API access page
                </a>
                , it takes a minute.
            </p>
            <div
                className="tw-flex tw-flex-wrap tw-gap-1.5"
                role="tablist"
                aria-label="Set up your tool"
            >
                {MCP_CLIENTS.map(client => (
                    <button
                        key={client.id}
                        type="button"
                        role="tab"
                        aria-selected={client.id === activeId}
                        onClick={() => setActiveId(client.id)}
                        className={choiceTabClass(client.id === activeId)}
                    >
                        {client.name}
                    </button>
                ))}
            </div>

            <ol
                className={`tw-m-0 tw-list-none tw-space-y-1.5 tw-p-0 ${META_TEXT} tw-text-muted-foreground`}
            >
                {active.steps.map((step, index) => (
                    <li key={step} className="tw-flex tw-gap-2">
                        <span className={ACCENT_TEXT}>{index + 1}.</span>
                        <span>{step}</span>
                    </li>
                ))}
            </ol>

            <button
                type="button"
                onClick={copySnippet}
                className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-background tw-px-3 tw-py-2 tw-text-left tw-text-[12px] tw-text-muted-foreground tw-transition-colors hover:tw-border-[#0089e0] dark:hover:tw-border-[#00b7ff]"
            >
                <span className="tw-truncate">{active.snippet}</span>
                {copied ? (
                    <Check
                        className={`tw-h-3.5 tw-w-3.5 tw-shrink-0 ${DONE_TEXT}`}
                        aria-hidden="true"
                    />
                ) : (
                    <CopyIcon
                        className="tw-h-3.5 tw-w-3.5 tw-shrink-0"
                        aria-hidden="true"
                    />
                )}
            </button>
        </div>
    )
}

/** Where the user is in the journey: the persona's steps, each done, current or upcoming. */
function StepTrail({ step, steps }) {
    const stateOf = index => {
        if (index < step) return 'done'
        if (index > step) return 'upcoming'
        return 'current'
    }
    return (
        <ol
            data-testid="guide-trail"
            aria-label="Guide steps"
            className={`tw-m-0 tw-flex tw-min-w-0 tw-list-none tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1 tw-p-0 ${META_TEXT}`}
        >
            {Object.entries(steps).map(([key, name], position) => {
                const index = Number(key)
                const state = stateOf(index)
                return (
                    <li
                        key={key}
                        data-step={index}
                        data-state={state}
                        aria-current={state === 'current' ? 'step' : undefined}
                        className="tw-inline-flex tw-items-center tw-gap-1.5"
                    >
                        {position > 0 ? (
                            <span
                                className="tw-text-muted-foreground"
                                aria-hidden="true"
                            >
                                ›
                            </span>
                        ) : null}
                        {state === 'done' ? (
                            <Check
                                className={`tw-h-3 tw-w-3 ${DONE_TEXT}`}
                                aria-hidden="true"
                            />
                        ) : null}
                        <span
                            className={
                                state === 'current'
                                    ? 'tw-font-medium tw-text-foreground'
                                    : 'tw-text-muted-foreground'
                            }
                        >
                            {name}
                        </span>
                    </li>
                )
            })}
        </ol>
    )
}

/** The ask as a callout; shows only the waiting line while an AI answer streams. */
function ActionBlock({ copy, aiAnswerState, observed }) {
    const accentText = copy.aiTone ? AI_ACCENT_TEXT : ACCENT_TEXT
    const tint = copy.aiTone ? AI_ACCENT_TINT : ACCENT_TINT
    const ActionIcon = copy.actionIcon === 'plug' ? Plug : ArrowRight
    const actionText = (observed && copy.actionObserved) || copy.action
    const aiLine =
        aiAnswerState === 'awaiting'
            ? copy.statusAwaitingAi
            : aiAnswerState === 'answered'
            ? copy.statusAnswered
            : null

    return (
        <div
            data-testid="guide-action-block"
            className={`tw-mt-4 tw-rounded-xl tw-px-3.5 tw-py-3 ${tint}`}
        >
            {aiLine ? (
                <p
                    data-testid="guide-awaiting"
                    data-state={aiAnswerState}
                    className={`tw-m-0 tw-flex tw-items-center tw-gap-2 ${BODY_TEXT} ${accentText}`}
                >
                    {aiAnswerState === 'awaiting' ? (
                        <Loader2
                            className="tw-h-4 tw-w-4 tw-shrink-0 tw-animate-spin"
                            aria-hidden="true"
                        />
                    ) : (
                        <Check
                            className={`tw-h-4 tw-w-4 tw-shrink-0 ${DONE_TEXT}`}
                            aria-hidden="true"
                        />
                    )}
                    <span>{aiLine}</span>
                </p>
            ) : (
                <>
                    <p
                        className={`tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 ${BODY_TEXT}`}
                    >
                        {copy.status ? (
                            <>
                                <span
                                    data-testid="guide-status"
                                    className="tw-inline-flex tw-items-center tw-gap-1 tw-text-foreground"
                                >
                                    <Check
                                        className={`tw-h-3.5 tw-w-3.5 ${DONE_TEXT}`}
                                        aria-hidden="true"
                                    />
                                    {copy.status}
                                </span>
                                <span
                                    className="tw-text-muted-foreground"
                                    aria-hidden="true"
                                >
                                    ·
                                </span>
                            </>
                        ) : null}
                        <span
                            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-font-medium ${accentText}`}
                        >
                            <ActionIcon
                                className="tw-h-4 tw-w-4 tw-shrink-0"
                                aria-hidden="true"
                            />
                            {actionText}
                        </span>
                    </p>
                    {copy.hint ? (
                        <p
                            className={`tw-m-0 tw-mt-1 tw-pl-[22px] ${META_TEXT} ${accentText}`}
                        >
                            {copy.hint}
                        </p>
                    ) : null}
                </>
            )}
        </div>
    )
}

/**
 * Presentational toast for one stage. The parent decides what the buttons do and
 * reports `aiAnswerState` ('idle' | 'awaiting' | 'answered') and `observed`
 * (the stage's precursor action happened).
 */
export default function GuideToast({
    copy,
    actionLabel,
    actionAltLabel,
    onAction,
    onActionAlt,
    onDismiss,
    showSetupCard = false,
    setupCardTab = null,
    steps = getStepNames(),
    aiAnswerState = 'idle',
    observed = false,
}) {
    const collapsed = useOnboardingStore(state => state.collapsed)
    const setCollapsed = useOnboardingStore(state => state.setCollapsed)
    const totalSteps = Object.keys(steps).length

    if (collapsed) {
        return (
            <ToastAnchor
                data-guide-toast=""
                className="tw-flex tw-justify-center"
            >
                <button
                    type="button"
                    onClick={() => setCollapsed(false)}
                    aria-label={
                        copy.step
                            ? `Reopen guide, step ${copy.step} of ${totalSteps}`
                            : 'Reopen guide'
                    }
                    className="tw-pointer-events-auto tw-flex tw-h-8 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-border-border tw-bg-background tw-pl-2.5 tw-pr-3.5 tw-shadow-md tw-transition-colors hover:tw-border-muted-foreground"
                >
                    <span
                        className={`tw-h-[7px] tw-w-[7px] tw-animate-pulse tw-rounded-full ${ACCENT_DOT}`}
                        aria-hidden="true"
                    />
                    <span className="tw-text-[11.5px] tw-font-medium tw-text-foreground">
                        Guide
                    </span>
                    {copy.step ? (
                        <span className="tw-text-[10.5px] tw-text-muted-foreground">
                            {copy.step}/{totalSteps}
                        </span>
                    ) : null}
                    <ChevronUp
                        className="tw-h-3 tw-w-3 tw-text-muted-foreground"
                        aria-hidden="true"
                    />
                </button>
            </ToastAnchor>
        )
    }

    const bodyParagraphs = Array.isArray(copy.body) ? copy.body : [copy.body]
    const showPrimaryButton = !!actionLabel && aiAnswerState === 'idle'

    return (
        <ToastCard
            eyebrow="First-Time User Guide"
            onClose={onDismiss}
            closeLabel="Close guide"
            tone={copy.aiTone ? 'ai' : 'default'}
            anchorProps={{ 'data-guide-toast': '' }}
        >
            {/* A <p>, not a heading: preflight is off, so a bare <h3> inherits the legacy heading rules. */}
            <p
                data-testid="guide-title"
                className={`tw-m-0 tw-mb-2 ${TITLE_TEXT}`}
            >
                {copy.title}
            </p>
            {copy.lede ? (
                <p className={`tw-m-0 tw-mb-2 ${BODY_TEXT} ${SECONDARY_TEXT}`}>
                    {copy.lede}
                </p>
            ) : null}
            {bodyParagraphs.map((paragraph, index) => {
                const text =
                    typeof paragraph === 'string' ? paragraph : paragraph.text
                const muted = typeof paragraph !== 'string' && paragraph.muted
                return (
                    <p
                        key={text}
                        className={[
                            'tw-m-0',
                            index > 0 ? 'tw-mt-2' : '',
                            BODY_TEXT,
                            muted ? SECONDARY_TEXT : '',
                        ].join(' ')}
                    >
                        {text}
                    </p>
                )
            })}

            {copy.languagePicker ? <LanguagePicker /> : null}

            {/* The setup card is the action block's expanded state. */}
            {showSetupCard ? (
                <McpSetupCard initialClientId={setupCardTab} />
            ) : (
                <ActionBlock
                    copy={copy}
                    aiAnswerState={aiAnswerState}
                    observed={observed}
                />
            )}

            {/* Two buttons leave the trail no room beside them. */}
            <div
                className={[
                    TOAST_FOOTER,
                    actionAltLabel
                        ? 'tw-flex-col tw-items-stretch'
                        : 'tw-items-start tw-justify-between',
                ].join(' ')}
            >
                {copy.step ? (
                    <StepTrail step={copy.step} steps={steps} />
                ) : (
                    <span
                        className={`tw-self-center ${META_TEXT} tw-text-muted-foreground`}
                    >
                        {copy.stepName}
                    </span>
                )}
                <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-end tw-gap-1.5">
                    {actionAltLabel ? (
                        <button
                            type="button"
                            data-testid="guide-action-alt"
                            onClick={onActionAlt}
                            className={GHOST_BUTTON}
                        >
                            {actionAltLabel}
                        </button>
                    ) : null}
                    {showPrimaryButton ? (
                        <button
                            type="button"
                            data-testid="guide-action"
                            onClick={onAction}
                            className={
                                copy.aiTone ? AI_ACTION_BUTTON : ACTION_BUTTON
                            }
                        >
                            {actionLabel}
                        </button>
                    ) : null}
                </div>
            </div>
        </ToastCard>
    )
}
